import React, { useEffect, useState, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { AuthContext, verifyAdminRole, type AdminUser } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import type { Session, AuthChangeEvent } from '@supabase/supabase-js';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const adminVerified = useRef(false);
  const verifyingMutex = useRef(false);
  const signInInProgress = useRef(false);
  const pendingSession = useRef<{ event: AuthChangeEvent; session: Session | null } | null>(null);

  // Separated async verification - triggered via pendingSession ref
  const processSession = useCallback(async (s: Session | null, event: AuthChangeEvent) => {
    if (!s) {
      adminVerified.current = false;
      setUser(null);
      setSession(null);
      setLoading(false);
      return;
    }

    if (event === 'TOKEN_REFRESHED') {
      setSession(s);
      return;
    }

    if (event === 'SIGNED_IN' && signInInProgress.current) {
      return;
    }

    if (adminVerified.current) {
      setSession(s);
      setLoading(false);
      return;
    }

    // Mutex to prevent double verification
    if (verifyingMutex.current) return;
    verifyingMutex.current = true;

    try {
      const isAdmin = await verifyAdminRole(s.user.id);
      if (isAdmin) {
        adminVerified.current = true;
        setUser({ id: s.user.id, email: s.user.email || '', role: 'admin' });
        setSession(s);
        Sentry.setUser({ id: s.user.id, email: s.user.email });
      } else {
        logger.warn('auth_not_admin', 'AuthProvider', { event, userId: s.user.id });
        adminVerified.current = false;
        setUser(null);
        setSession(null);
        supabase.auth.signOut();
      }
    } catch (err) {
      logger.error('auth_verify_failed', 'AuthProvider', { event, error: String(err) });
      adminVerified.current = false;
      setUser(null);
      setSession(null);
      supabase.auth.signOut();
    } finally {
      verifyingMutex.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Single initialization path via onAuthStateChange
    // INITIAL_SESSION event fires on mount with the cached session (Supabase v2.39+)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'SIGNED_OUT') {
        adminVerified.current = false;
        setUser(null);
        setSession(null);
        setLoading(false);
        Sentry.setUser(null);
        logger.info('signed_out', 'AuthProvider', {});
        return;
      }

      // Defer async work outside the callback to avoid Supabase deadlocks
      pendingSession.current = { event, session: s };
      processSession(s, event);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [processSession]);

  const signIn = useCallback(async (email: string, password: string): Promise<string | null> => {
    signInInProgress.current = true;
    try {
      adminVerified.current = false;

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return error.message;
      if (!data.user) return 'Connexion échouée.';

      const isAdmin = await verifyAdminRole(data.user.id);
      if (!isAdmin) {
        await supabase.auth.signOut();
        return 'Accès réservé aux administrateurs.';
      }

      adminVerified.current = true;
      const adminUser = { id: data.user.id, email: data.user.email || email, role: 'admin' };
      setUser(adminUser);
      setSession(data.session);
      Sentry.setUser({ id: adminUser.id, email: adminUser.email });
      return null;
    } catch (err) {
      logger.error('sign_in', 'AuthProvider', { error: String(err) });
      return String(err);
    } finally {
      signInInProgress.current = false;
    }
  }, []);

  const signOut = useCallback(async () => {
    adminVerified.current = false;
    setUser(null);
    setSession(null);
    Sentry.setUser(null);
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
